import { useState, useMemo } from 'react'
import { Head, Link, usePage } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Search, ArrowRight, BookOpen, Layers, CheckCircle2,
  Atom, Landmark, TrendingUp, GraduationCap, Stethoscope,
  ShieldCheck, Clock, Award
} from 'lucide-react'
import type { PageProps } from '@/types'

interface ExamItem {
  title: string
  slug: string
  code: string
  category: string
  description: string
  is_live: boolean
  badge: string
  tests_count: number
  target_audience: string
}

interface CategoryGroup {
  id: string
  name: string
  description: string
  exams: ExamItem[]
}

interface PublicExamsIndexProps {
  categories: CategoryGroup[]
  seo: {
    title: string
    description: string
    keywords: string
  }
}

function getShortCategoryLabel(catId: string, fullName: string) {
  switch (catId) {
    case 'engineering':
      return 'Engineering'
    case 'banking':
      return 'Banking'
    case 'financial':
      return 'Finance & NISM'
    case 'civil-services':
      return 'Civil Services'
    case 'medical':
      return 'Medical'
    default:
      return fullName.split(' (')[0]
  }
}

function getCategoryIcon(catId: string) {
  switch (catId) {
    case 'engineering':
      return Atom
    case 'banking':
      return Landmark
    case 'financial':
      return TrendingUp
    case 'civil-services':
      return GraduationCap
    case 'medical':
      return Stethoscope
    default:
      return BookOpen
  }
}

export default function PublicExamsIndex({ categories = [], seo }: PublicExamsIndexProps) {
  const { auth } = usePage<PageProps>().props
  usePublicPage()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const allExams = useMemo(() => {
    return categories.flatMap(cat => cat.exams.map(e => ({ ...e, categoryId: cat.id })))
  }, [categories])

  const filteredCategories = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()

    return categories
      .filter(cat => selectedCategory === 'all' || cat.id === selectedCategory)
      .map(cat => {
        const matchingExams = cat.exams.filter(e =>
          !q ||
          e.title.toLowerCase().includes(q) ||
          e.code.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.target_audience.toLowerCase().includes(q)
        )
        return { ...cat, exams: matchingExams }
      })
      .filter(cat => cat.exams.length > 0)
  }, [categories, searchQuery, selectedCategory])

  // JSON-LD Structured Data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Indian Competitive Exams Directory',
    description: seo.description,
    url: 'https://prepkiya.com/exams',
    numberOfItems: categories.reduce((acc, cat) => acc + cat.exams.length, 0),
    itemListElement: categories.flatMap((cat, catIdx) =>
      cat.exams.map((exam, examIdx) => ({
        '@type': 'ListItem',
        position: catIdx * 10 + examIdx + 1,
        name: exam.title,
        url: `https://prepkiya.com/exams/${exam.slug}`
      }))
    )
  }

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://prepkiya.com'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Exams',
        item: 'https://prepkiya.com/exams'
      }
    ]
  }

  return (
    <>
      <Head title={seo.title}>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />
        <link rel="canonical" href="https://prepkiya.com/exams" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content="https://prepkiya.com/exams" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }}
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-brand-gold/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 flex flex-col">
          {/* Header Hero Section matching LandingHero UX */}
          <section className="w-full pt-8 pb-10 sm:pt-16 sm:pb-20 bg-white border-b border-slate-100">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-3xl mx-auto text-center space-y-3 sm:space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-[11px] sm:text-xs font-bold tracking-wide uppercase shadow-2xs">
                  <GraduationCap className="w-3.5 h-3.5 text-[#c8933b]" />
                  <span>National Examination Directory</span>
                </div>
                
                <h1 className="!font-sans text-2xl sm:text-4xl lg:text-[46px] font-black text-[#131b2e] tracking-tight leading-tight">
                  Explore Indian Competitive Exams & Mock Test Hubs
                </h1>
                
                <p className="!font-sans text-xs sm:text-lg text-slate-600 leading-relaxed font-normal max-w-2xl mx-auto">
                  Access official exam blueprints, sectional time-management strategies, and online practice mock tests with PrepKiya&apos;s 4-method solutions.
                </p>
              </div>

              {/* Search & Category Pills Bar */}
              <div className="mt-6 sm:mt-10 max-w-4xl mx-auto space-y-3 sm:space-y-4">
                <div className="relative w-full">
                  <Search className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 absolute left-3.5 sm:left-4 top-1/2 -translate-y-1/2" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search exams (e.g. GATE 2027, SBI Clerk, NISM)..."
                    className="!font-sans pl-10 sm:pl-12 h-11 sm:h-12 bg-[#f8fafc] border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl text-xs sm:text-base focus:border-[#131b2e] focus:ring-1 focus:ring-[#131b2e] shadow-2xs"
                  />
                </div>

                {/* Filter Pills: horizontally scrollable on mobile without awkward wrap or scrollbars */}
                <div className="flex items-center sm:justify-center gap-2 pt-1 overflow-x-auto whitespace-nowrap pb-1 no-scrollbar">
                  <button
                    type="button"
                    onClick={() => setSelectedCategory('all')}
                    className={`!font-sans px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer shrink-0 ${
                      selectedCategory === 'all'
                        ? 'bg-[#131b2e] text-white shadow-sm'
                        : 'bg-[#f8fafc] border border-slate-200 text-slate-700 hover:text-[#131b2e] hover:border-slate-300'
                    }`}
                  >
                    All ({allExams.length})
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`!font-sans px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold transition-all cursor-pointer shrink-0 ${
                        selectedCategory === cat.id
                          ? 'bg-[#131b2e] text-white shadow-sm'
                          : 'bg-[#f8fafc] border border-slate-200 text-slate-700 hover:text-[#131b2e] hover:border-slate-300'
                      }`}
                    >
                      {getShortCategoryLabel(cat.id, cat.name)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Categories Grid matching ExamCategoriesSection UX */}
          <section className="w-full py-10 sm:py-20 bg-[#f8fafc] border-b border-slate-200/80">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 sm:space-y-14">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-12 sm:py-16 bg-white border border-slate-200 rounded-2xl sm:rounded-3xl p-6 sm:p-12 space-y-3 sm:space-y-4 shadow-sm max-w-lg mx-auto">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#f4f7fb] border border-slate-200 text-[#131b2e] flex items-center justify-center mx-auto">
                    <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-[#c8933b]" />
                  </div>
                  <h3 className="!font-sans text-lg sm:text-xl font-bold text-[#131b2e]">No examinations match your search</h3>
                  <p className="!font-sans text-xs sm:text-sm text-slate-600">Try searching for &ldquo;GATE&rdquo;, &ldquo;SBI&rdquo;, &ldquo;Bank&rdquo;, or reset filters to see all available categories.</p>
                  <Button
                    variant="outline"
                    onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                    className="!font-sans font-bold border-slate-300 hover:bg-slate-50 text-[#131b2e] rounded-xl h-9 sm:h-10 px-4 sm:px-5 text-xs"
                  >
                    Reset All Filters
                  </Button>
                </div>
              ) : (
                filteredCategories.map((cat) => {
                  const Icon = getCategoryIcon(cat.id)
                  return (
                    <div key={cat.id} className="space-y-4 sm:space-y-6">
                      <div className="flex items-center gap-3 pb-3 border-b border-slate-200">
                        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-gold/15 border border-brand-gold/30 text-brand-gold flex items-center justify-center shadow-2xs shrink-0">
                          <Icon className="w-5 h-5 stroke-[2.2]" />
                        </div>
                        <div>
                          <h2 className="!font-sans text-xl sm:text-3xl font-black text-[#131b2e] tracking-tight">
                            {cat.name}
                          </h2>
                          <p className="!font-sans text-[11px] sm:text-sm text-slate-600 mt-0.5">
                            {cat.description}
                          </p>
                        </div>
                      </div>

                      {/* 3-Column Grid matching ExamCategoriesSection */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-7">
                        {cat.exams.map((exam) => (
                          <div
                            key={exam.slug}
                            className="bg-white border border-slate-200 rounded-2xl sm:rounded-3xl p-5 sm:p-7 flex flex-col justify-between shadow-xs hover:shadow-md hover:border-brand-gold/60 transition-all duration-200 space-y-4 sm:space-y-5"
                          >
                            <div className="space-y-3 sm:space-y-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className="!font-sans inline-flex items-center px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-bold bg-[#f4f7fb] text-[#131b2e] border border-slate-200 shadow-2xs truncate max-w-[200px]">
                                  {exam.badge}
                                </span>
                                {exam.is_live && exam.tests_count > 0 ? (
                                  <span className="!font-sans inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold bg-brand-gold/15 text-brand-gold border border-brand-gold/30 shadow-2xs shrink-0">
                                    {exam.tests_count} Test{exam.tests_count > 1 ? 's' : ''}
                                  </span>
                                ) : (
                                  <span className="!font-sans inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200 shrink-0">
                                    Coming Soon
                                  </span>
                                )}
                              </div>

                              <div className="space-y-1.5 sm:space-y-2">
                                <h3 className="!font-sans text-lg sm:text-xl font-bold text-[#131b2e] line-clamp-1" title={exam.title}>
                                  {exam.title}
                                </h3>
                                <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-2">
                                  {exam.description}
                                </p>
                              </div>

                              <div className="pt-1 text-[11px] sm:text-xs text-slate-500 flex items-center gap-1.5">
                                <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 shrink-0" />
                                <span className="truncate font-medium">Target: {exam.target_audience}</span>
                              </div>
                            </div>

                            <div className="pt-4 sm:pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 border-t border-slate-100 mt-2 sm:mt-5">
                              {exam.is_live ? (
                                <>
                                  <Link
                                    href={`/exams/${exam.slug}`}
                                    className="!font-sans text-xs font-bold text-slate-700 hover:text-brand-gold flex items-center justify-between sm:justify-start gap-1 transition-colors py-1"
                                  >
                                    <span>Exam Pattern & Guide</span>
                                    <ArrowRight className="w-3.5 h-3.5" />
                                  </Link>

                                  <Button
                                    asChild
                                    className="!font-sans w-full sm:w-auto bg-[#131b2e] hover:bg-[#1e293b] text-white font-bold rounded-xl sm:rounded-lg px-4 h-9 text-xs shadow-sm cursor-pointer transition-all"
                                  >
                                    <Link href={`/exams/${exam.slug}`}>
                                      Start Test
                                    </Link>
                                  </Button>
                                </>
                              ) : (
                                <div className="w-full flex items-center justify-between text-xs text-slate-400">
                                  <span className="font-medium text-slate-400 text-[11px] sm:text-xs">Curriculum Roadmap</span>
                                  <span className="text-[10px] sm:text-[11px] font-semibold bg-slate-100 text-slate-500 px-2 py-0.5 sm:py-1 rounded-md">
                                    Coming Soon
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </section>

          {/* Pillars matching MissionPricingSection */}
          <section className="w-full py-12 sm:py-24 bg-white border-t border-slate-100">
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 sm:space-y-12">
              <div className="text-center max-w-3xl mx-auto space-y-2 sm:space-y-3">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f4f7fb] border border-slate-200 text-[#131b2e] text-[11px] sm:text-xs font-bold tracking-wide uppercase">
                  <Award className="w-3.5 h-3.5 text-[#c8933b]" />
                  <span>Why Aspirants Choose PrepKiya</span>
                </div>
                <h2 className="!font-sans text-2xl sm:text-4xl font-black text-[#131b2e] tracking-tight leading-tight">
                  High-Fidelity Preparation Engineered for Rankers
                </h2>
                <p className="!font-sans text-xs sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
                  Every feature is built around the actual examination hall reality — from strict sectional timer locks to multi-angle question explanations.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 pt-1 sm:pt-2">
                <div className="p-4 sm:p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 sm:mb-4 shadow-2xs">
                      <Layers className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-sm sm:text-base text-[#131b2e] mb-1.5 sm:mb-2">
                      4-Method Step Solutions
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Go beyond single textbook derivations. Master questions via First-Principles, 10-second Speed Shortcuts, Boundary Case Checks, and Visual Logic.
                    </p>
                  </div>
                </div>

                <div className="p-4 sm:p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 sm:mb-4 shadow-2xs">
                      <Clock className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-sm sm:text-base text-[#131b2e] mb-1.5 sm:mb-2">
                      Official Sectional Timers
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Experience strict non-switchable section countdowns (20 mins per section in Bank PO & Clerk, 180 mins global in GATE) to build true stamina.
                    </p>
                  </div>
                </div>

                <div className="p-4 sm:p-6 rounded-2xl bg-[#f8fafc] border border-slate-200 hover:border-[#131b2e] hover:shadow-md transition-all flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-white border border-slate-200 text-[#131b2e] flex items-center justify-center mb-3 sm:mb-4 shadow-2xs">
                      <ShieldCheck className="w-5 h-5 text-[#c8933b]" />
                    </div>
                    <h4 className="!font-sans font-bold text-sm sm:text-base text-[#131b2e] mb-1.5 sm:mb-2">
                      Accurate Negative Marking
                    </h4>
                    <p className="!font-sans text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Exact scoring algorithms calibrated with official examination bodies with instant percentile ranking across verified student attempts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
